// hooks/useFlightSearch.ts
import { useMutation, useQuery } from "@tanstack/react-query";
import api from "@/app/api";
import {
  FlightSearchRequest,
  FlightSearchResponse,
} from "@/types/search/flight";
import useFlightBookingStore, {
  FareRuleRequest,
  FareRuleResponse,
} from "@/lib/useFlightBookingStore";

export function useFlightSearch() {
  const { setSearchedFlight, setIsLoading, setFareRule, setIsFareRuleLoading } =
    useFlightBookingStore();

  const searchFlights = useMutation({
    mutationKey: ["searchFlights"],
    mutationFn: async (searchParams: FlightSearchRequest) => {
      setIsLoading(true);
      try {
        const response = await api.post<FlightSearchResponse>(
          "/flight/search",
          searchParams,
        );
        setSearchedFlight(response.data);
        return response.data;
      } finally {
        setIsLoading(false);
      }
    },
  });

  const searchCalendar = useMutation({
    mutationKey: ["searchCalendar"],
    mutationFn: async (searchParams: FlightSearchRequest) => {
      const response = await api.post<FlightSearchResponse>(
        "/flight/search/calendar",
        searchParams,
      );
      return response.data;
    },
  });

  const rescheduleSearch = useMutation({
    mutationKey: ["rescheduleSearch"],
    mutationFn: async (searchParams: any) => {
      setIsLoading(true);
      const payload = {
        paxInfo: searchParams.paxInfo,
        routeInfos: searchParams.routeInfos,
        pnr: searchParams.pnr,
        oldBookingId: searchParams.oldBookingId,
        paxIds: searchParams.paxIds.map(String), // safety
        metadata: searchParams.metadata,
      };
      try {
        const response = await api.post<FlightSearchResponse>(
          "/flight/reschedule/search",
          payload,
        );
        if (response.data?.status?.success === false) {
          throw new Error(
            response.data.errors?.[0]?.message ||
              "No reissue results found for the given booking",
          );
        }
        setSearchedFlight(response.data);
        return response.data;
      } finally {
        setIsLoading(false);
      }
    },
  });

  const getFareRules = useMutation({
    mutationKey: ["fareRules"],
    mutationFn: async (data: FareRuleRequest) => {
      setIsFareRuleLoading(true);
      try {
        const response = await api.post<FareRuleResponse>(
          "/flight/farerule",
          data,
        );
        setFareRule(response.data);
        return response.data;
      } finally {
        setIsFareRuleLoading(false);
      }
    },
  });

  const getBookingFareRules = useMutation({
    mutationKey: ["bookingFareRules"],
    mutationFn: async (data: FareRuleRequest) => {
      setIsFareRuleLoading(true);
      try {
        const response = await api.post<FareRuleResponse>(
          "/flight/farerule",
          data,
        );
        return response.data;
      } finally {
        setIsFareRuleLoading(false);
      }
    },
  });

  const submitRescheduleAmendment = useMutation({
    mutationKey: ["submitRescheduleAmendment"],
    mutationFn: async (
      data: any,
    ): Promise<{ data: { amendmentId: string; bookingId: number } }> => {
      const response = await api.post<{
        data: { amendmentId: string; bookingId: number };
      }>("/flight/booking/amendment/reschedule", data);
      return response.data;
    },
  });

  return {
    getFareRules: getFareRules.mutateAsync,
    searchFlights: searchFlights.mutateAsync,
    getBookingFareRules: getBookingFareRules.mutateAsync,
    submitRescheduleAmendment: submitRescheduleAmendment.mutateAsync,
    isSubmittingManualRequest: submitRescheduleAmendment.isPending,
    isLoading: searchFlights.isPending || rescheduleSearch.isPending,
    error: searchFlights.error || rescheduleSearch.error,
    data: searchFlights.data || rescheduleSearch.data,
    isFareRuleLoading: getFareRules.isPending,
    searchCalendar: searchCalendar.mutateAsync,
    rescheduleSearch: rescheduleSearch.mutateAsync,
  };
}
